import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Collapse,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  PictureAsPdf as PdfIcon,
  Assignment as AssignmentIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Person as PersonIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import api, {
  getScholarships,
  getScholarshipStats,
  updateScholarshipStatus,
  deleteScholarship,
} from "../services/api";

interface Scholarship {
  _id: string;
  fullName: string;
  cnic: string;
  rollNumber: string;
  email: string;
  mobileNumber: string;
  challanNumber: string;
  imagePath?: string;
  status: "pending" | "approved" | "rejected";
  appliedAt: string;
}

interface ScholarshipStats {
  total: number;
  pending: number;
  approved: number;
  rejected: number;
  recent: number;
}

function ScholarshipRow({
  scholarship,
  getInitials,
  formatDate,
  handleStatusChange,
  handleDelete,
}: {
  scholarship: Scholarship;
  getInitials: (name: string) => string;
  formatDate: (date: string) => string;
  handleStatusChange: (scholarship: Scholarship, newStatus: string) => void;
  handleDelete: (scholarship: Scholarship) => void;
}) {
  const [open, setOpen] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return { color: "#059669", bg: "rgba(16,185,129,0.1)", icon: <CheckCircleIcon sx={{ fontSize: 16 }} /> };
      case "rejected":
        return { color: "#ef4444", bg: "rgba(239,68,68,0.1)", icon: <CancelIcon sx={{ fontSize: 16 }} /> };
      default:
        return { color: "#f59e0b", bg: "rgba(245,158,11,0.1)", icon: <PendingIcon sx={{ fontSize: 16 }} /> };
    }
  };

  const statusStyle = getStatusColor(scholarship.status);

  return (
    <React.Fragment>
      <TableRow
        hover
        sx={{
          "& > *": { borderBottom: "unset" },
          bgcolor: open ? "rgba(241, 245, 249, 0.5)" : "inherit",
          transition: "background-color 0.2s",
        }}
      >
        <TableCell padding="checkbox">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              ml: 1,
              color: open ? "#059669" : "#94a3b8",
              bgcolor: open ? "rgba(5,150,105,0.1)" : "transparent",
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box display="flex" alignItems="center">
            <Avatar
              sx={{
                mr: 2,
                bgcolor: "primary.main",
                width: 42,
                height: 42,
                fontWeight: 700,
                fontSize: "1rem",
                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
              }}
            >
              {scholarship.fullName ? getInitials(scholarship.fullName) : <PersonIcon />}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0f172a", fontSize: "0.95rem" }}>
                {scholarship.fullName}
              </Typography>
              <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 500 }}>
                {scholarship.email}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={`Roll No: ${scholarship.rollNumber}`}
            sx={{ height: 24, fontSize: "0.75rem", bgcolor: "#f1f5f9", color: "#475569", fontWeight: 600, mb: 0.5 }}
          />
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#334155", display: "block" }}>
            {scholarship.mobileNumber}
          </Typography>
        </TableCell>
        <TableCell>
          <Chip
            icon={statusStyle.icon}
            label={scholarship.status.charAt(0).toUpperCase() + scholarship.status.slice(1)}
            size="small"
            sx={{
              bgcolor: statusStyle.bg,
              color: statusStyle.color,
              fontWeight: 700,
              px: 0.5,
            }}
          />
        </TableCell>
        <TableCell>
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#475569", fontSize: "0.85rem" }}>
            {formatDate(scholarship.appliedAt)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Box display="flex" gap={1} justifyContent="flex-end" sx={{ pr: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setOpen(!open)}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                borderColor: "#cbd5e1",
                color: "#475569",
              }}
            >
              {open ? "Hide Details" : "Review Application"}
            </Button>
          </Box>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box
              sx={{
                margin: 2,
                p: 3,
                bgcolor: "#f8fafc",
                borderRadius: "16px",
                border: "1px solid #e2e8f0",
                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                pb={2}
                borderBottom="1px solid #e2e8f0"
              >
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a" }}>
                  Application Details
                </Typography>
                <Box display="flex" gap={1}>
                  {scholarship.status !== "approved" && (
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        bgcolor: "#10b981",
                        "&:hover": { bgcolor: "#059669" },
                        borderRadius: "8px",
                        textTransform: "none",
                        boxShadow: "none",
                      }}
                      startIcon={<CheckCircleIcon />}
                      onClick={() => handleStatusChange(scholarship, "approved")}
                    >
                      Approve
                    </Button>
                  )}
                  {scholarship.status !== "rejected" && (
                    <Button
                      size="small"
                      variant="contained"
                      sx={{
                        bgcolor: "#f59e0b",
                        "&:hover": { bgcolor: "#d97706" },
                        borderRadius: "8px",
                        textTransform: "none",
                        boxShadow: "none",
                      }}
                      startIcon={<CancelIcon />}
                      onClick={() => handleStatusChange(scholarship, "rejected")}
                    >
                      Reject
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    sx={{ borderRadius: "8px", textTransform: "none" }}
                    onClick={() => handleDelete(scholarship)}
                  >
                    Delete
                  </Button>
                </Box>
              </Box>

              <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={4}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontWeight: 700,
                      mb: 1.5,
                      fontSize: "0.75rem",
                    }}
                  >
                    Applicant Information
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Full Name:</Typography>
                      <Typography variant="body2" fontWeight={600}>{scholarship.fullName}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">CNIC:</Typography>
                      <Typography variant="body2" fontWeight={600}>{scholarship.cnic}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Email:</Typography>
                      <Typography variant="body2" fontWeight={600}>{scholarship.email}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">Mobile Number:</Typography>
                      <Typography variant="body2" fontWeight={600}>{scholarship.mobileNumber}</Typography>
                    </Box>
                  </Stack>
                </Box>

                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "#64748b",
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontWeight: 700,
                      mb: 1.5,
                      fontSize: "0.75rem",
                    }}
                  >
                    Registration & Verification
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">Roll Number:</Typography>
                      <Chip
                        size="small"
                        label={scholarship.rollNumber}
                        sx={{ fontWeight: 600, height: 22, bgcolor: "#e0f2fe", color: "#0369a1" }}
                      />
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">Challan Number:</Typography>
                      <Typography variant="body2" fontWeight={600}>{scholarship.challanNumber}</Typography>
                    </Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="text.secondary">Application Date:</Typography>
                      <Typography variant="body2" fontWeight={600}>{formatDate(scholarship.appliedAt)}</Typography>
                    </Box>

                    {scholarship.imagePath && (
                      <Box mt={2}>
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          Attached Document:
                        </Typography>
                        <Box
                          component="a"
                          href={`https://backend.hunarmandpunjab.org.pk${scholarship.imagePath}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            p: 1.5,
                            bgcolor: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            textDecoration: "none",
                            color: "#3b82f6",
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            "&:hover": { bgcolor: "#f1f5f9" },
                          }}
                        >
                          <PdfIcon sx={{ fontSize: 20, color: "#ef4444" }} />
                          View Document
                        </Box>
                      </Box>
                    )}
                  </Stack>
                </Box>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

function Applications() {
  const [scholarships, setScholarships] = useState<Scholarship[]>([]);
  const [stats, setStats] = useState<ScholarshipStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Pagination & Filtering state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Modals state
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState<Scholarship | null>(null);
  const [newStatus, setNewStatus] = useState("");
  const [actionReason, setActionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, statusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError("");
      
      // Fetch stats
      const statsRes = await getScholarshipStats();
      if (statsRes.data.success) {
        setStats(statsRes.data.data);
      }

      // Fetch applications list
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
      };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const res = await getScholarships(params);
      if (res.data.success) {
        setScholarships(res.data.data.scholarships);
        setTotal(res.data.data.pagination.total);
      }
    } catch (err) {
      console.error("Error fetching applications:", err);
      setError("Failed to load applications data.");
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const downloadCSV = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;

      const response = await api.get("/scholarships/download", {
        params,
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      
      const timestamp = new Date().toISOString().split("T")[0];
      link.download = `scholarships_export_${timestamp}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading CSV:", err);
      alert("Failed to download CSV.");
    } finally {
      setLoading(false);
    }
  };

  // Status Change Dialog Handlers
  const handleOpenStatusChange = (app: Scholarship, status: string) => {
    setSelectedApp(app);
    setNewStatus(status);
    setActionReason("");
    setStatusUpdateOpen(true);
  };

  const confirmStatusChange = async () => {
    if (!selectedApp) return;
    try {
      setActionLoading(true);
      await updateScholarshipStatus(selectedApp._id, newStatus, actionReason);
      setStatusUpdateOpen(false);
      fetchData();
    } catch (err) {
      console.error("Error updating status:", err);
      alert("Failed to update status. Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  // Delete Dialog Handlers
  const handleOpenDelete = (app: Scholarship) => {
    setSelectedApp(app);
    setActionReason("");
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedApp) return;
    try {
      setActionLoading(true);
      await deleteScholarship(selectedApp._id);
      setDeleteOpen(false);
      fetchData();
    } catch (err) {
      console.error("Error deleting application:", err);
      alert("Failed to delete application.");
    } finally {
      setActionLoading(false);
    }
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ fontFamily: "'Plus Jakarta Sans',sans-serif", pb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={3}>
        <Box display="flex" alignItems="center">
          <Box sx={{ bgcolor: 'rgba(5, 150, 105, 0.1)', p: 1.5, borderRadius: '12px', mr: 2 }}>
            <AssignmentIcon sx={{ fontSize: 32, color: '#059669' }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
              Applications
            </Typography>
            <Typography sx={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500, mt: 0.25 }}>
              Manage and review student scholarship applications
            </Typography>
          </Box>
        </Box>
        <Tooltip title="Refresh Data">
          <IconButton onClick={fetchData} disabled={loading} sx={{ bgcolor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Summary Cards */}
      <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr 1fr" }} gap={2} mb={3}>
        {[
          { title: "Total Applications", value: stats?.total || 0, icon: <AssignmentIcon />, color: "#3b82f6", bg: "rgba(59,130,246,0.08)" },
          { title: "Pending Review", value: stats?.pending || 0, icon: <PendingIcon />, color: "#f59e0b", bg: "rgba(245,158,11,0.08)" },
          { title: "Approved", value: stats?.approved || 0, icon: <CheckCircleIcon />, color: "#10b981", bg: "rgba(16,185,129,0.08)" },
          { title: "Rejected", value: stats?.rejected || 0, icon: <CancelIcon />, color: "#ef4444", bg: "rgba(239,68,68,0.08)" },
        ].map((item, index) => (
          <motion.div key={item.title} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
            <Card
              sx={{
                borderRadius: "16px",
                bgcolor: "white",
                border: "1px solid rgba(226, 232, 240, 0.8)",
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
                height: "100%",
                overflow: "hidden"
              }}
            >
              <Box sx={{ height: 3, bgcolor: item.color }} />
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: "10px",
                      bgcolor: item.bg,
                      color: item.color,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {React.cloneElement(item.icon as React.ReactElement<any>, { sx: { fontSize: 24 } })}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#0f172a", lineHeight: 1.2 }}>
                      {item.value.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "#475569" }}>{item.title}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </Box>

      {/* Filters and Actions */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: "16px",
            border: "1px solid rgba(226, 232, 240, 0.8)",
            boxShadow: "0 4px 15px rgba(0,0,0,0.02)",
          }}
        >
          <Box display="flex" flexDirection={{ xs: "column", md: "row" }} gap={2} alignItems={{ xs: "stretch", md: "center" }}>
            <Box display="flex" alignItems="center" sx={{ flex: 1, position: "relative" }}>
              <SearchIcon sx={{ position: "absolute", left: 14, color: "#94a3b8" }} />
              <input
                type="text"
                placeholder="Search by Name, Roll No, CNIC, Email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "12px 14px 12px 45px",
                  borderRadius: "12px",
                  border: "1px solid #cbd5e1",
                  fontSize: "0.95rem",
                  outline: "none",
                  fontFamily: "inherit",
                }}
              />
            </Box>

            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{ borderRadius: "12px" }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadCSV}
              sx={{
                height: 54,
                borderRadius: "12px",
                textTransform: "none",
                fontWeight: 600,
                color: "#475569",
                borderColor: "#cbd5e1",
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              Export CSV
            </Button>
          </Box>
        </Paper>
      </motion.div>

      {/* Data Table */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid rgba(226, 232, 240, 0.8)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.03)",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell width={40} />
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Applicant Profile</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Contact & Roll No</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700, color: "#475569" }}>Applied Date</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, color: "#475569" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : scholarships.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5, color: "#64748b" }}>
                    No applications found matching your criteria.
                  </TableCell>
                </TableRow>
              ) : (
                scholarships.map((app) => (
                  <ScholarshipRow
                    key={app._id}
                    scholarship={app}
                    getInitials={getInitials}
                    formatDate={formatDate}
                    handleStatusChange={handleOpenStatusChange}
                    handleDelete={handleOpenDelete}
                  />
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ borderTop: "1px solid #e2e8f0" }}
        />
      </Paper>

      {/* Status Update Dialog */}
      <Dialog open={statusUpdateOpen} onClose={() => !actionLoading && setStatusUpdateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: "#0f172a" }}>
          Confirm Status Change
        </DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Are you sure you want to change the status of <b>{selectedApp?.fullName}</b> to{" "}
            <b style={{ color: newStatus === "approved" ? "#059669" : "#ef4444" }}>
              {newStatus.toUpperCase()}
            </b>
            ?
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Reason (Optional)"
            placeholder="Enter reason for audit logs"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            InputProps={{ sx: { borderRadius: "12px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setStatusUpdateOpen(false)} color="inherit" disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmStatusChange}
            disabled={actionLoading}
            sx={{
              bgcolor: newStatus === "approved" ? "#10b981" : "#ef4444",
              "&:hover": { bgcolor: newStatus === "approved" ? "#059669" : "#dc2626" },
              borderRadius: "8px",
              boxShadow: "none",
            }}
          >
            {actionLoading ? "Updating..." : "Confirm Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={() => !actionLoading && setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, color: "#dc2626" }}>
          Delete Application
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>
            This action cannot be undone. This will permanently delete the application for <b>{selectedApp?.fullName}</b>.
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Reason (Required for deletion)"
            placeholder="Why is this application being deleted?"
            value={actionReason}
            onChange={(e) => setActionReason(e.target.value)}
            InputProps={{ sx: { borderRadius: "12px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={() => setDeleteOpen(false)} color="inherit" disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmDelete}
            disabled={actionLoading || !actionReason.trim()}
            sx={{
              bgcolor: "#ef4444",
              "&:hover": { bgcolor: "#dc2626" },
              borderRadius: "8px",
              boxShadow: "none",
            }}
          >
            {actionLoading ? "Deleting..." : "Permanently Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Applications;
